/**
 * Trunnion AI - Agent Detail Page
 */

import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Trash2 } from 'lucide-react';
import { AgentDetailView } from '@/components/agents/AgentDetailView';
import { CreateAgentWizard } from '@/components/agents/CreateAgentWizard';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAgent, useDeleteAgent } from '@/hooks/queries/useAgents';
import { ROUTES } from '@/config/routes';

export default function AgentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const { data: agent, isLoading, error } = useAgent(isNew ? '' : (id ?? ''));
  const deleteAgent = useDeleteAgent();

  if (isNew) {
    return (
      <div>
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate(ROUTES.AGENTS)}
            className="mb-2"
          >
            Back to Agents
          </Button>
          <h1 className="text-2xl font-bold text-white">Create Agent</h1>
        </div>
        <CreateAgentWizard />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size="lg" label="Loading agent..." />
      </div>
    );
  }

  if (error || !agent) {
    return (
      <EmptyState
        message="Agent not found"
        description="The requested agent could not be loaded."
        action={
          <Button onClick={() => navigate(ROUTES.AGENTS)}>Back to Agents</Button>
        }
      />
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          icon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate(ROUTES.AGENTS)}
        >
          Back to Agents
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" icon={<Edit2 className="h-4 w-4" />}>
            Edit
          </Button>
          <Button
            variant="danger"
            size="sm"
            icon={<Trash2 className="h-4 w-4" />}
            onClick={() => {
              deleteAgent.mutate(agent.id, {
                onSuccess: () => navigate(ROUTES.AGENTS),
              });
            }}
            loading={deleteAgent.isPending}
          >
            Delete
          </Button>
        </div>
      </div>
      <AgentDetailView agent={agent} />
    </div>
  );
}
